import { ApolloCache, FetchResult } from '@apollo/client';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { computed, Ref } from 'vue';

import {
  ContextInput,
  CreatePostMutation,
  PostQuery,
  PostsQuery,
  PostsQueryVariables,
  UpdatePostMutation
} from '@/__generated__/gateway/graphql';
import { ContentModel } from '@/components/community/utils/types';
import { useContentImageUpload } from '@/shared/composables/community/images';
import {
  createPostMutation,
  deletePostMutation,
  updatePostMutation
} from '@/shared/graphql/gateway/community/mutations';
import {
  postQuery,
  postsQuery
} from '@/shared/graphql/gateway/community/queries';
import {
  isTosNotAcceptedError,
  isUserBlockedError,
  useCommunityDispatchUtils
} from '@/shared/graphql/gateway/utils';
import { NestedTypeByPath } from '@/shared/utils/typescript';

type CreatePostMutationResult = Omit<
  FetchResult<CreatePostMutation>,
  'context'
>;
type UpdatePostMutationResult = Omit<
  FetchResult<UpdatePostMutation>,
  'context'
>;

type CommonPostCacheTools = {
  queryVariables: PostsQueryVariables;
  restart: () => void;
};

const DEFAULT_POSTS_LIMIT = 5;

export type CommunityPost = NestedTypeByPath<PostQuery, ['community', 'post']>;

export const useCommunityPost = (postId: string) => {
  const {
    loading,
    result,
    error: fetchError,
    onResult
  } = useQuery(postQuery, { postId }, { clientId: 'gateway' });

  const post = computed(() => result.value?.community?.post ?? null);

  const onPost = (callback: (post: CommunityPost | null) => void) =>
    onResult((res) => {
      // Note: For some odd reason, `onResult` can fire WITHOUT the data property defined.
      // When this happens the response is still `loading`..?
      if (!res.loading) {
        callback(res.data.community?.post ?? null);
      }
    });

  const { dispatchMutationWithTosRetry } = useCommunityDispatchUtils();

  const { uploadImages, uploading } = useContentImageUpload();

  const {
    mutate: dispatchUpdatePost,
    loading: updating,
    error: updateError
  } = useMutation(updatePostMutation, { clientId: 'gateway' });

  const updatePost = async (postId: string, model: ContentModel) => {
    const images = await uploadImages(model.images);

    const res = await dispatchMutationWithTosRetry(() =>
      dispatchUpdatePost(
        {
          input: {
            id: postId,
            body: model.body,
            images
          }
        },
        {
          // post mutation cache update functionality
          update: updateCacheUpdate()
        }
      )
    );

    if (res?.errors?.length) {
      throw res.errors[0];
    }
  };

  const {
    mutate: dispatchDeletePost,
    loading: deleting,
    error: deleteError
  } = useMutation(deletePostMutation, { clientId: 'gateway' });

  const deletePost = async (postId: string) => {
    const res = await dispatchMutationWithTosRetry(() =>
      dispatchDeletePost(
        {
          postId
        },
        {
          // post mutation cache update functionality
          update: deletionCacheUpdate(postId)
        }
      )
    );

    if (res?.errors?.length) {
      throw res.errors[0];
    }
  };

  const error = computed(() => {
    const err = fetchError.value || updateError.value || deleteError.value;

    if (isTosNotAcceptedError(err) || isUserBlockedError(err)) {
      // Both are handled in the dispatcher (TOS retries; blocked shows a
      // modal and swallows). Don't surface them as a generic error here.
      return null;
    }
    return err;
  });

  const mutating = computed(
    () => uploading.value || updating.value || deleting.value
  );

  const processing = computed(() => loading.value || mutating.value);

  return {
    loading,
    mutating,
    processing,
    error,
    post,
    onPost,
    deletePost,
    updatePost
  };
};

export type CommunityPostsOptions = {
  limit?: number;
  userId?: string;
};

export const useCommunityPosts = (
  context: ContextInput,
  options: CommunityPostsOptions = {},
  enabled: boolean | Ref<boolean> = true
) => {
  const queryVariables: PostsQueryVariables = {
    userId: options.userId,
    limit: options.limit ?? DEFAULT_POSTS_LIMIT,
    context
  };

  const { loading, result, error, onResult, fetchMore, restart } = useQuery(
    postsQuery,
    queryVariables,
    { clientId: 'gateway', enabled }
  );

  const posts = computed<CommunityPost[]>(
    () => result.value?.community?.posts?.posts ?? []
  );

  const onPosts = (callback: (posts: CommunityPost[]) => void) =>
    onResult((res) => {
      // Note: For some odd reason, `onResult` can fire WITHOUT the data property defined.
      // When this happens the response is still `loading`..?
      if (!res.loading) {
        callback(res.data.community?.posts?.posts ?? []);
      }
    });

  // NOTE: if entity is not a part of the PostsQueryVariables, this will always be null
  const total = computed(
    () => result.value?.community?.posts?.total ?? undefined
  );
  const loadMoreCursor = computed(
    () => result.value?.community?.posts?.cursor ?? null
  );
  const hasMore = computed(() => !!loadMoreCursor.value);

  const loadMore = async () => {
    if (loadMoreCursor.value) {
      await fetchMore({
        variables: {
          cursor: loadMoreCursor.value
        },
        updateQuery: appendToExistingData
      });
    }
  };

  const cacheTools: CommonPostCacheTools = {
    queryVariables,
    restart
  };

  const { dispatchMutationWithTosRetry } = useCommunityDispatchUtils();

  const { uploadImages, uploading } = useContentImageUpload();

  const { mutate: dispatchCreatePost, loading: creating } = useMutation(
    createPostMutation,
    { clientId: 'gateway' }
  );

  const createPost = async (model: ContentModel) => {
    const images = await uploadImages(model.images);

    const res = await dispatchMutationWithTosRetry(() =>
      dispatchCreatePost(
        {
          input: {
            body: model.body,
            images
          },
          context
        },
        {
          // post mutation cache update functionality
          update: creationCacheUpdate(cacheTools)
        }
      )
    );

    if (res?.errors?.length) {
      throw res.errors[0];
    }
  };

  const { mutate: dispatchUpdatePost, loading: updating } = useMutation(
    updatePostMutation,
    { clientId: 'gateway' }
  );

  const updatePost = async (postId: string, model: ContentModel) => {
    const images = await uploadImages(model.images);

    const res = await dispatchMutationWithTosRetry(() =>
      dispatchUpdatePost(
        {
          input: {
            id: postId,
            body: model.body,
            images
          }
        },
        {
          // post mutation cache update functionality
          update: updateCacheUpdate()
        }
      )
    );

    if (res?.errors?.length) {
      throw res.errors[0];
    }
  };

  const { mutate: dispatchDeletePost, loading: deleting } = useMutation(
    deletePostMutation,
    { clientId: 'gateway' }
  );

  const deletePost = async (postId: string) => {
    const res = await dispatchMutationWithTosRetry(() =>
      dispatchDeletePost(
        {
          postId
        },
        {
          // post mutation cache update functionality
          update: deletionCacheUpdate(postId, cacheTools.queryVariables)
        }
      )
    );

    if (res?.errors?.length) {
      throw res.errors[0];
    }
  };

  const mutating = computed(
    () => uploading.value || creating.value || updating.value || deleting.value
  );

  const processing = computed(() => loading.value || mutating.value);

  return {
    loading,
    mutating,
    processing,
    error,
    posts,
    onPosts,
    total,
    hasMore,
    loadMore,
    createPost,
    updatePost,
    deletePost
  };
};

/**
 * Appends a fetchMore result to existing data
 * @param previousResult current data
 * @param options containing fetchMoreResult
 */
const appendToExistingData = (
  previousResult: PostsQuery,
  {
    fetchMoreResult
  }: {
    fetchMoreResult?: PostsQuery;
  }
) => {
  // No new posts, return previous
  if (
    !previousResult?.community?.posts?.posts ||
    !fetchMoreResult?.community?.posts?.posts
  ) {
    return previousResult;
  }

  return {
    ...fetchMoreResult,
    community: {
      ...fetchMoreResult.community,
      posts: {
        ...fetchMoreResult.community.posts,
        posts: [
          ...previousResult.community.posts.posts,
          ...fetchMoreResult.community.posts.posts
        ]
      }
    }
  };
};

const creationCacheUpdate =
  ({ queryVariables, restart }: CommonPostCacheTools) =>
  <T>(cache: ApolloCache<T>, result: CreatePostMutationResult) => {
    const createdPost = result.data?.community?.createPost;

    if (!createdPost) {
      return;
    }

    const postsCacheOptions = {
      query: postsQuery,
      // NOTE: reuse variables, the cache is of the exact query & variables
      variables: queryVariables
    };
    const postsCache = cache.readQuery(postsCacheOptions);
    const community = postsCache?.community;
    const postsWrapper = community?.posts;
    const posts = postsWrapper?.posts;

    if (!postsWrapper) {
      console.error('postsWrapper is undefined');
    }

    if (postsWrapper) {
      cache.writeQuery({
        ...postsCacheOptions,
        data: {
          community: {
            ...community,
            posts: {
              ...postsWrapper,
              total: community.posts?.total ? community.posts.total + 1 : null,
              posts: [createdPost, ...(posts || [])]
            }
          }
        }
      });
    } else {
      restart();
    }
  };

const updateCacheUpdate =
  () =>
  <T>(cache: ApolloCache<T>, result: UpdatePostMutationResult) => {
    const updatedPost = result.data?.community?.updatePost;

    if (!updatedPost) {
      return;
    }

    cache.modify({
      id: cache.identify(updatedPost),
      fields: {
        body() {
          return updatedPost.body;
        },
        images() {
          return updatedPost.images ?? null;
        }
      },
      broadcast: false
    });
  };

const deletionCacheUpdate =
  (postId: string, queryVariables?: PostsQueryVariables) =>
  <T>(cache: ApolloCache<T>) => {
    const matcher = { __typename: 'Post', id: postId };
    cache.evict({ id: cache.identify(matcher) });

    // update total (TODO: any easier way to achieve this?)
    if (queryVariables) {
      const postsCacheOptions = {
        query: postsQuery,
        // NOTE: reuse variables, the cache is of the exact query & variables
        variables: queryVariables
      };
      const postsCache = cache.readQuery(postsCacheOptions);
      const community = postsCache?.community;
      const postsWrapper = community?.posts;

      if (!postsWrapper) {
        console.error('postsWrapper is undefined');
      }

      if (postsWrapper) {
        cache.writeQuery({
          ...postsCacheOptions,
          data: {
            community: {
              ...community,
              posts: {
                ...postsWrapper,
                total: community.posts?.total ? community.posts.total - 1 : null
              }
            }
          }
        });
      }
    }
  };
